import { useEffect, useState } from 'react';

import { Icon, Icons } from '@/components/icon';
import { Button, Modal, TextArea } from '@/components/ui/design-system';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';

const SENTIMENTS = [1, 2, 3, 4, 5] as const;
type Sentiment = (typeof SENTIMENTS)[number];

const REASONS = ['cant_hear_them', 'they_cant_hear_me', 'robotic_voice', 'other'] as const;
type Reason = (typeof REASONS)[number];

const FEEDBACK_MAX_LENGTH = 500;

type CallQualityFeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  callId?: string | null;
  deviceLegId?: string | null;
};

export const CallQualityFeedbackModal = ({
  isOpen,
  onClose,
  callId,
  deviceLegId,
}: CallQualityFeedbackModalProps) => {
  const { t } = useTranslation('desktop_calls');
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [reasons, setReasons] = useState<Set<Reason>>(new Set());
  const [feedback, setFeedback] = useState('');

  // Reset transient form state whenever the modal closes so a follow-up open
  // starts blank.
  useEffect(() => {
    if (!isOpen) {
      setSentiment(null);
      setReasons(new Set());
      setFeedback('');
    }
  }, [isOpen]);

  const trimmedFeedback = feedback.trim();
  const requiresFeedback = reasons.has('other');
  const isSubmitDisabled = sentiment === null || (requiresFeedback && trimmedFeedback.length === 0);

  const toggleReason = (reason: Reason) => {
    setReasons((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) {
        next.delete(reason);
      } else {
        next.add(reason);
      }
      return next;
    });
  };

  const handleDismiss = () => {
    analytics.track(ANALYTICS_EVENTS.CALL_QUALITY_FEEDBACK.DISMISSED);
    onClose();
  };

  const handleSubmit = () => {
    if (isSubmitDisabled || sentiment === null) {
      return;
    }
    const reasonsList = Array.from(reasons);
    analytics.track(ANALYTICS_EVENTS.CALL_QUALITY_FEEDBACK.SUBMITTED, {
      sentiment,
      reasons: reasonsList,
      reasons_count: reasonsList.length,
      has_feedback_text: trimmedFeedback.length > 0,
    });
    tracingService.trackEvent(CallEventName.FeedbackReported, CallEventLevel.Info, {
      sentiment,
      reasons: reasonsList,
      feedback: trimmedFeedback.length > 0 ? trimmedFeedback : null,
      ...(callId ? { callId } : {}),
      ...(deviceLegId ? { deviceLegId } : {}),
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      variant="content"
      title={t('call_quality_feedback.modal.title')}
      subTitle={t('call_quality_feedback.modal.subtitle')}
      size="sm"
      actions={[
        <Button key="cancel" variant="outline" size="sm" type="button" onClick={handleDismiss}>
          {t('call_quality_feedback.modal.cancel')}
        </Button>,
        <Button
          key="submit"
          variant="primary"
          size="sm"
          type="button"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {t('call_quality_feedback.modal.submit')}
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-body-13 text-primary-70">
            {t('call_quality_feedback.modal.sentiment_label')}
          </span>
          <div className="flex items-center gap-2">
            {SENTIMENTS.map((value) => {
              const isFilled = sentiment !== null && value <= sentiment;
              return (
                <button
                  key={value}
                  type="button"
                  aria-label={t('call_quality_feedback.modal.sentiment_option', { value })}
                  aria-pressed={sentiment === value}
                  onClick={() => setSentiment(value)}
                  className={cn(
                    'flex size-9 cursor-pointer items-center justify-center rounded-lg border transition-colors',
                    isFilled
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 text-primary-40 hover:bg-primary-5',
                  )}
                >
                  <Icon name={isFilled ? Icons.StarFill : Icons.Star} className="text-m" />
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-body-13 text-primary-70">
            {t('call_quality_feedback.modal.reasons_label')}
          </span>
          <div className="flex flex-wrap gap-2">
            {REASONS.map((reason) => {
              const isSelected = reasons.has(reason);
              return (
                <button
                  key={reason}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggleReason(reason)}
                  className={cn(
                    'cursor-pointer rounded-full border px-3 py-1.5 text-body-13 transition-colors',
                    isSelected
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 text-primary-70 hover:bg-primary-5',
                  )}
                >
                  {t(`call_quality_feedback.modal.reasons.${reason}`)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-body-13 text-primary-70">
            {requiresFeedback
              ? t('call_quality_feedback.modal.feedback_label_required')
              : t('call_quality_feedback.modal.feedback_label')}
          </span>
          <TextArea
            fit="parent"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t('call_quality_feedback.modal.placeholder')}
            rows={4}
            maxLength={FEEDBACK_MAX_LENGTH}
          />
        </div>
      </div>
    </Modal>
  );
};
